import React from 'react';
import { 
  CreditCard, 
  DollarSign, 
  AlertCircle,
  Banknote,
  Wallet,
  Building2,
  Smartphone,
  CheckCircle
} from 'lucide-react';

const PaymentMethodSection = ({ 
  reservationSettings,
  reservationData,
  handleEnhancedInputChange,
  fieldValidation,
  isSaving,
  uploadingDocuments
}) => {
  // Get enabled payment methods
  const getEnabledPaymentMethods = () => {
    return (reservationSettings.paymentMethods || []).filter(method => method && method.enabled === true);
  };

  // Get default payment method
  const getDefaultPaymentMethod = () => {
    const methods = getEnabledPaymentMethods();
    const defaultMethod = methods.find(m => m.isDefault);
    return defaultMethod || methods[0] || null;
  };

  // Get icon component based on type
  const getPaymentIcon = (type) => {
    switch (type) {
      case 'cash':
        return DollarSign;
      case 'credit_card':
        return CreditCard;
      case 'debit_card':
        return CreditCard;
      case 'bank_transfer':
        return Building2;
      case 'mobile_payment':
        return Smartphone;
      case 'wallet':
        return Wallet;
      default:
        return Banknote;
    }
  };

  const enabledMethods = getEnabledPaymentMethods();
  
  // Don't render section if no payment methods configured or enabled
  if (!enabledMethods || enabledMethods.length === 0) {
    return null;
  }

  const selectedMethod = reservationData.paymentMethod;
  const hasError = fieldValidation?.paymentMethod?.hasError;

  return (
    <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-600">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-white font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-orange-400" />
            Payment Method
          </h4>
          <p className="text-gray-400 text-sm mt-1">
            Select the payment method for this reservation
          </p>
        </div>
        <div className="flex items-center gap-2 bg-green-900/30 text-green-300 px-3 py-1 rounded-full text-xs border border-green-700">
          <CheckCircle size={12} />
          <span>{enabledMethods.length} {enabledMethods.length === 1 ? 'method' : 'methods'} available</span>
        </div>
      </div>

      {/* Payment Methods Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {enabledMethods.map((method) => {
          const IconComponent = getPaymentIcon(method.type);
          const isSelected = selectedMethod === method.name;
          const isDefault = method.isDefault;

          return (
            <div
              key={method.id}
              onClick={() => {
                if (!isSaving && !uploadingDocuments) {
                  handleEnhancedInputChange({
                    target: {
                      name: 'paymentMethod',
                      value: method.name
                    }
                  });
                }
              }}
              className={`
                relative cursor-pointer rounded-lg p-4 border-2 transition-all duration-200
                ${isSelected 
                  ? 'border-orange-500 bg-orange-900/30 shadow-lg shadow-orange-500/20' 
                  : 'border-gray-600 bg-gray-700/30 hover:border-gray-500 hover:bg-gray-700/50'
                }
                ${(isSaving || uploadingDocuments) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02]'}
              `}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <CheckCircle className="w-5 h-5 text-orange-400 fill-orange-400" />
                </div>
              )}

              {/* Default Badge */}
              {isDefault && !isSelected && (
                <div className="absolute top-2 right-2">
                  <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Default
                  </span>
                </div>
              )}

              {/* Icon and Name */}
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isSelected 
                    ? 'bg-orange-500/20' 
                    : 'bg-gray-600/30'
                }`}>
                  <IconComponent className={`w-6 h-6 ${
                    isSelected ? 'text-orange-400' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className={`font-bold ${isSelected ? 'text-orange-300' : 'text-white'}`}>
                    {method.name}
                  </div>
                  <div className="text-xs text-gray-400 capitalize">
                    {method.type.replace('_', ' ')}
                  </div>
                </div>
              </div>

              {/* Description */}
              {method.description && (
                <p className={`text-xs mt-2 ${
                  isSelected ? 'text-orange-200/80' : 'text-gray-400'
                }`}>
                  {method.description}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Validation Error */}
      {hasError && (
        <div className="mt-3 bg-red-900/30 border border-red-700 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle size={14} />
            <span>{fieldValidation.paymentMethod.message}</span>
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-4 bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-300">
            <p className="font-medium mb-1">Payment Method Selection</p>
            <p className="text-blue-400/80">
              {selectedMethod 
                ? `Selected: ${selectedMethod}` 
                : `Default: ${getDefaultPaymentMethod()?.name || 'None'}`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSection;